import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Maximize2, Minimize2 } from "lucide-react";

// Right slide-out detail panel — slides in only when `open`, mirrors the MyHub pattern.
// Full-screen toggle, Esc/X/backdrop close, full width on mobile.
export function DetailPanel({ open, onClose, eyebrow, title, icon: Icon, actions, children, testid = "detail-panel", width = "w-full sm:w-[520px] lg:w-[47%] xl:w-[42%]" }) {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => { if (!open) setExpanded(false); }, [open]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (e.key === "Escape") {
        if (expanded) setExpanded(false);
        else onClose?.();
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, expanded, onClose]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            onClick={onClose}
            data-testid={`${testid}-backdrop`}
            className={`fixed inset-0 z-[95] ${expanded ? "bg-neutral-900/95" : "bg-slate-900/40"}`}
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.22, ease: "easeOut" }}
            style={{ willChange: "transform" }}
            data-testid={testid}
            className={`fixed top-0 bottom-0 right-0 bg-white dark:bg-slate-950 z-[100] shadow-2xl flex flex-col ${expanded ? "left-0 w-full" : width}`}
          >
            <div className="flex items-start gap-3 p-5 border-b border-slate-200 dark:border-slate-800">
              <div className="min-w-0 flex-1">
                {eyebrow && <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{eyebrow}</p>}
                <div className="flex items-center gap-2 mt-1">
                  {Icon && <Icon className="h-6 w-6 text-blue-600 shrink-0" />}
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white truncate">{title}</h2>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {actions}
                <button
                  data-testid={`${testid}-fullscreen`}
                  onClick={() => setExpanded((e) => !e)}
                  title={expanded ? "Restore" : "Full screen"}
                  className="h-9 w-9 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  {expanded ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
                </button>
                <button
                  data-testid={`${testid}-close`}
                  onClick={onClose}
                  className="h-9 w-9 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className={`flex-1 min-w-0 overflow-y-auto overflow-x-hidden overscroll-contain ${expanded ? "max-w-[1200px] w-full mx-auto" : ""} p-5 bg-neutral-50 dark:bg-slate-900`}>{children}</div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
}
